// src/components/customer/CustomerSampleForm.js

import React, { useEffect, useMemo } from "react";
import {
  Package,
  Send,
  UserCheck,
  Trash2,
  Calendar,
  Building2,
  Box,
  XCircle,
} from "lucide-react";

import DeliveryDatePicker from "../sampleorders/DeliveryDatePicker";

const getPrimaryContact = (account) => {
  const pc = account?.primaryContact || {};
  return {
    name: pc.name || account?.contactName || account?.contact || "",
    title: pc.title || account?.contactTitle || "",
    email: pc.email || account?.contactEmail || account?.email || "",
    phone: pc.phone || account?.contactPhone || account?.phone || "",
  };
};

const SectionCard = ({ title, icon: Icon, right, children }) => {
  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {Icon ? <Icon className="w-4 h-4 text-blue-600" /> : null}
          <div className="text-xs font-semibold text-gray-900 tracking-wide uppercase truncate">
            {title}
          </div>
        </div>
        {right ? <div className="shrink-0">{right}</div> : null}
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
};

// Keep exactly ONE blank row at the bottom, and never allow 0 rows
const normalizeItems = (arr) => {
  const items = Array.isArray(arr) ? [...arr] : [];
  if (items.length === 0) items.push({ productId: "", quantity: 1 });

  // Remove extra trailing blank rows (keep only one)
  while (
    items.length > 1 &&
    !items[items.length - 1]?.productId &&
    !items[items.length - 2]?.productId
  ) {
    items.pop();
  }

  // Ensure there is always a blank row at the end
  const last = items[items.length - 1];
  if (last?.productId) {
    items.push({ productId: "", quantity: 1 });
  }

  // Ensure quantities are sane
  return items.map((it) => ({
    productId: it?.productId || "",
    quantity:
      it?.quantity === ""
        ? ""
        : Number(it?.quantity) && Number(it?.quantity) > 0
        ? Number(it?.quantity)
        : 1,
  }));
};

// ✅ IMPORTANT: only submit VALID items (strip the blank trailing row)
const getCleanItemsForSubmit = (items) => {
  const base = Array.isArray(items) ? items : [];
  return base
    .filter((i) => i?.productId && Number(i?.quantity) > 0)
    .map((i) => ({
      productId: String(i.productId),
      quantity: Number(i.quantity) > 0 ? Number(i.quantity) : 1,
    }));
};

const CustomerSampleForm = ({
  form,
  inventory,
  accounts,
  repUser,
  saving,
  onSubmit,
  onChange,
  onClear,
  deliveryDays,
  sampleRequestLeadDays,
  kits = [],
  requireReasonForSample = false,
  maxItemsPerOrder = null,
}) => {
  const groupMode = form.groupMode || "single";
  const effectiveMaxItems =
    typeof maxItemsPerOrder === "number" && maxItemsPerOrder > 0 ? maxItemsPerOrder : null;

  useEffect(() => {
    if (!form.groupMode) {
      onChange("groupMode", "single");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.groupMode]);

  // Always render with a trailing blank row ready to fill
  const items = useMemo(() => {
    const base =
      (form.items && form.items.length > 0
        ? form.items
        : [
            {
              productId: form.productId || "",
              quantity: form.quantity || 1,
            },
          ]) || [];

    return normalizeItems(base);
  }, [form.items, form.productId, form.quantity]);

  const handleItemsChange = (updatedItems) => {
    const normalized = normalizeItems(updatedItems);
    onChange("items", normalized);

    // Keep legacy fields in sync (use FIRST valid item, not necessarily index 0)
    const firstValid =
      normalized.find((i) => i.productId && Number(i.quantity) > 0) || null;

    if (firstValid) {
      onChange("productId", firstValid.productId || "");
      onChange("quantity", firstValid.quantity || 1);
    } else {
      onChange("productId", "");
      onChange("quantity", 1);
    }
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    const current = updated[index] || { productId: "", quantity: 1 };

    updated[index] = {
      ...current,
      [field]:
        field === "quantity" ? (value === "" ? "" : Number(value) || 1) : value,
    };

    handleItemsChange(updated);
  };

  const handleRemoveItem = (index) => {
    const updated = items.filter((_, i) => i !== index);
    handleItemsChange(updated.length ? updated : [{ productId: "", quantity: 1 }]);
  };

  const validItemCount = items.filter(
    (i) => i.productId && Number(i.quantity) > 0
  ).length;

  const selectedKit =
    form.kitId && kits?.length ? kits.find((k) => k.id === form.kitId) : null;

  const handleKitChange = (value) => {
    if (!value) {
      onChange("kitId", "");
      return;
    }

    const kit = kits.find((k) => k.id === value) || null;
    onChange("kitId", value);

    if (kit && Array.isArray(kit.items) && kit.items.length > 0) {
      const mappedItems = kit.items.map((kitItem) => ({
        productId: kitItem.productId || "",
        quantity:
          typeof kitItem.quantity === "number" && kitItem.quantity > 0
            ? kitItem.quantity
            : 1,
      }));
      handleItemsChange(mappedItems);
    }
  };

  const selectedAccount =
    form.accountId && accounts?.length
      ? accounts.find((a) => a.id === form.accountId) || null
      : null;

  const handleAccountChange = (value) => {
    const selected = accounts.find((acc) => acc.id === value) || null;

    // ✅ Store multiple variants so other screens (map) can match reliably
    onChange("accountId", value);
    onChange("accountName", selected?.name || "");

    // Back-compat / map-compat fields (harmless if unused)
    onChange("locationId", value);
    onChange("locationName", selected?.name || "");
    onChange("accountIds", value ? [value] : []);
  };

  const handleLocalSubmit = (e) => {
    // ✅ submit ONLY valid items (strip the trailing blank row)
    const cleanItems = getCleanItemsForSubmit(items);

    // Just in case
    if (!form.accountId || !form.requestedDate || cleanItems.length === 0) {
      e.preventDefault();
      return;
    }

    if (effectiveMaxItems && cleanItems.length > effectiveMaxItems) {
      e.preventDefault();
      return;
    }

    const accountName =
      selectedAccount?.name || form.accountName || form.locationName || "";

    // ✅ Pass a richer payload so your save logic can’t “miss” the linkage
    const submitMeta = {
      groupMode: groupMode || "single",
      kitId: form.kitId || null,

      // linkage fields
      accountId: form.accountId,
      accountName,
      accountIds: [form.accountId],
      locationId: form.accountId,
      locationName: accountName,

      // order fields
      requestedDate: form.requestedDate,
      notes: form.notes || "",
      items: cleanItems,

      // legacy single-item fields (if any older code still uses these)
      productId: cleanItems[0]?.productId || "",
      quantity: cleanItems[0]?.quantity || 1,

      // optional rep assignment info (if your save logic uses it)
      assignedToUserId: repUser?.id || repUser?.uid || null,
      assignedToName: repUser?.fullName || repUser?.email || "",
    };

    console.log("[CustomerSampleForm] submitMeta:", submitMeta);

    onSubmit(e, submitMeta);
  };

  const submitDisabled =
    saving || validItemCount === 0 || !form.accountId || !form.requestedDate;

  const contact = selectedAccount ? getPrimaryContact(selectedAccount) : null;

  const hasAnythingToClear = useMemo(() => {
    const hasItems =
      Array.isArray(form.items) && form.items.length
        ? form.items.some((i) => i?.productId || Number(i?.quantity) > 1)
        : !!form.productId || Number(form.quantity) > 1;

    const hasOther = !!form.requestedDate || !!form.notes || !!form.kitId;

    return hasItems || hasOther;
  }, [form]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-visible">
      {/* Slim header */}
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Send className="w-4 h-4 text-blue-600" />
            <h3 className="font-semibold text-gray-900 text-sm truncate">
              New Sample Order
            </h3>
          </div>
          <p className="mt-0.5 text-xs text-gray-500">
            Multiple line items, one request.
          </p>
        </div>

        <div className="shrink-0 flex items-center gap-2">
          {typeof onClear === "function" ? (
            <button
              type="button"
              onClick={onClear}
              disabled={saving || !hasAnythingToClear}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Clear this sample order"
            >
              <XCircle className="w-4 h-4" />
              Clear
            </button>
          ) : null}
        </div>
      </div>

      <div className="p-4 overflow-visible">
        {(inventory || []).length === 0 ? (
          <div className="text-center py-10">
            <Package className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-700 font-medium">
              No customer-visible sample items available
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Your distributor must mark inventory items as{" "}
              <span className="font-semibold">Sample-able</span> AND{" "}
              <span className="font-semibold">Visible to Customers</span>.
            </p>
          </div>
        ) : (
          <form
            onSubmit={handleLocalSubmit}
            className="grid grid-cols-1 lg:grid-cols-12 gap-4 overflow-visible"
          >
            {/* LEFT: Items + Notes */}
            <div className="lg:col-span-7 space-y-4">
              <SectionCard
                title="Sample items"
                icon={Package}
                right={
                  kits?.length > 0 ? (
                    <div className="flex items-center gap-1.5 text-[11px] text-gray-600">
                      <span className="hidden sm:inline">Kit</span>
                      <select
                        className="border border-gray-300 rounded-md px-2 py-1 text-xs bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={form.kitId || ""}
                        onChange={(e) => handleKitChange(e.target.value)}
                      >
                        <option value="">None</option>
                        {kits.map((kit) => (
                          <option key={kit.id} value={kit.id}>
                            {kit.name || "Untitled kit"}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : null
                }
              >
                {selectedKit ? (
                  <div className="mb-2 text-[11px] text-blue-900 bg-blue-50 border border-blue-100 rounded-md px-2.5 py-1.5">
                    Using kit:{" "}
                    <span className="font-semibold">
                      {selectedKit.name || "Untitled kit"}
                    </span>
                  </div>
                ) : null}

                <div className="hidden md:grid grid-cols-[1fr_90px_40px] gap-2 text-[11px] text-gray-500 px-1 mb-1">
                  <span>Product</span>
                  <span>Qty</span>
                  <span className="text-right"> </span>
                </div>

                <div className="space-y-2">
                  {items.map((item, index) => {
                    const isLast = index === items.length - 1;
                    const isBlank = !item.productId;

                    return (
                      <div key={index} className="flex items-start gap-2">
                        <div className="flex-1">
                          <select
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                            value={item.productId}
                            onChange={(e) =>
                              handleItemChange(index, "productId", e.target.value)
                            }
                          >
                            <option value="">Select product</option>
                            {(inventory || []).map((inv) => (
                              <option key={inv.id} value={inv.id}>
                                {inv.name} {inv.sku && `(${inv.sku})`}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="w-[92px]">
                          <input
                            type="number"
                            min="1"
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            value={item.quantity}
                            onChange={(e) =>
                              handleItemChange(index, "quantity", e.target.value)
                            }
                          />
                        </div>

                        {isLast && isBlank ? (
                          <div className="w-10 h-[40px]" />
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(index)}
                            className="w-10 h-[40px] flex items-center justify-center rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors border border-transparent hover:border-red-100"
                            aria-label="Remove item"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="mt-2 flex items-center justify-between text-[11px] text-gray-500">
                  <div>
                    {validItemCount > 0 ? (
                      <span>
                        {validItemCount} valid item
                        {validItemCount > 1 ? "s" : ""}
                      </span>
                    ) : (
                      <span>Select at least one product</span>
                    )}
                  </div>
                  <div className="text-right">
                    {effectiveMaxItems ? (
                      <div className="text-amber-600">Limit: {effectiveMaxItems} items per order</div>
                    ) : null}
                    <div className="text-gray-400">Tip: Use kits for common bundles</div>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-2 mb-1">
                    <Box className="w-4 h-4 text-blue-600" />
                  <div className="text-[11px] font-semibold text-gray-700 tracking-wide uppercase">
                      {requireReasonForSample ? "Reason for sample" : "Notes"}
                    </div>
                  </div>
                  <textarea
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows="2"
                    value={form.notes || ""}
                    onChange={(e) => onChange("notes", e.target.value)}
                    placeholder={
                      requireReasonForSample
                        ? "Tell your rep why you need this sample."
                        : "Optional: special instructions, preferences, etc."
                    }
                  />
                  {requireReasonForSample ? (
                    <p className="mt-2 text-[11px] text-amber-700">
                      A reason is required before this request can be submitted.
                    </p>
                  ) : null}
                </div>
              </SectionCard>
            </div>

            {/* RIGHT: Customer + Scheduling */}
            <div className="lg:col-span-5 space-y-4">
              <SectionCard title="Customer & assignment" icon={Building2}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <label className="block text-[11px] font-medium text-gray-600 mb-1">
                      Location
                    </label>
                    <select
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      value={form.accountId || ""}
                      onChange={(e) => handleAccountChange(e.target.value)}
                      required
                    >
                      <option value="">Select location</option>
                      {(accounts || []).map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.name}
                        </option>
                      ))}
                    </select>

                    {selectedAccount ? (
                      <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 p-2.5">
                        <div className="text-xs font-semibold text-gray-900 truncate">
                          {selectedAccount.name || "Untitled location"}
                        </div>

                        <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-gray-700">
                          {contact?.name ? (
                            <div className="sm:col-span-2">
                              <span className="text-gray-500">
                                Point of Contact:
                              </span>{" "}
                              <span className="font-medium">{contact.name}</span>
                              {contact.title ? (
                                <span className="text-gray-500 ml-1">
                                  ({contact.title})
                                </span>
                              ) : null}
                            </div>
                          ) : null}

                          {contact?.email ? (
                            <div className="truncate">
                              <span className="text-gray-500">Email:</span>{" "}
                              {contact.email}
                            </div>
                          ) : null}

                          {contact?.phone ? (
                            <div className="truncate">
                              <span className="text-gray-500">Phone:</span>{" "}
                              {contact.phone}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-[11px] font-medium text-gray-600 mb-1">
                      Assign To <span className="text-gray-400">(locked)</span>
                    </label>

                    <div className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-50 flex items-center gap-2 text-gray-900">
                      <UserCheck className="w-4 h-4 text-blue-600" />
                      <span className="truncate">
                        {repUser?.fullName || repUser?.email || "To be assigned"}
                      </span>
                    </div>

                    <div className="text-[11px] text-gray-500 mt-1">
                      Your distributor assigns your rep.
                    </div>
                  </div>
                </div>
              </SectionCard>

              <SectionCard title="Scheduling" icon={Calendar}>
                <DeliveryDatePicker
                  value={form.requestedDate}
                  onChange={(dateStr) => onChange("requestedDate", dateStr)}
                  deliveryDays={deliveryDays}
                  leadDays={sampleRequestLeadDays}
                />
              </SectionCard>
            </div>

            {/* Submit bar */}
            <div className="lg:col-span-12">
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="text-xs text-gray-600">
                  {validItemCount > 0 ? (
                    <span>
                      Ready to submit{" "}
                      <span className="font-semibold text-gray-900">
                        {validItemCount}
                      </span>{" "}
                      item{validItemCount > 1 ? "s" : ""}
                      {form.accountId ? (
                        <span className="text-gray-400"> • </span>
                      ) : null}
                      {form.accountId ? (
                        <span className="text-gray-700">Location selected</span>
                      ) : (
                        <span className="text-gray-500">Select a location</span>
                      )}
                    </span>
                  ) : (
                    <span className="text-gray-500">
                      Add at least one valid item to submit
                    </span>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={submitDisabled}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                >
                  <Send className="w-4 h-4" />
                  {saving
                    ? "Submitting..."
                    : validItemCount > 1
                    ? `Submit (${validItemCount})`
                    : "Submit"}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CustomerSampleForm;
